<template>
  <messenger-widget-email-view
    :view-settings="emailViewSettings"
    :is-loading="isLoading"
    :attachments="attachments"
    :scroll-bar-id="messengerEmailScrollBarId"
    @submit="submit"
    @back="$emit('back')"
    @files-picked="uploadAttachments"
    @remove-attachment="removeAttachment"
    @retry-attachment="retryAttachment"
    @resize="$emit('resize', $event)"
  >
    <template v-slot:alert>
      <messenger-alert-container :scopes="['email']" />
    </template>
  </messenger-widget-email-view>
</template>

<script setup lang="ts">
import type { MessengerEmailForm } from '@widgets/messenger/types'
import translateService from '@widgets/shared/services/translate/translateService'
import messengerApiService from '@widgets/messenger/services/messengerApiService'
import MessengerWidgetEmailView from '@widgets/messenger/components/views/MessengerWidgetEmailView.vue'
import { useWidgetEmailViewSettings } from '@widgets/messenger/compositions/useWidgetEmailViewSettings'
import { useFormFieldMap } from '@widgets/messenger/compositions/useFormFieldMap'
import { isValidEmail } from '@widgets/shared/utils/validators/isValidEmail'
import { castCommonErrorToMessages } from '@widgets/messenger/utils/castCommonErrorToMessages'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { useWidgetFormAlerts } from '@widgets/messenger/compositions/useWidgetFormAlerts'
import MessengerAlertContainer from '@widgets/messenger/containers/MessengerAlertContainer.vue'
import { useWidgetEmailAttachments } from '@widgets/messenger/compositions/useWidgetEmailAttachments'
import type { WidgetAttachment } from '@widgets/shared/types'
import type { MessengerSendEmailRequest } from '@widgets/messenger/types/api/rest'
import { messengerEmailScrollBarId } from '@widgets/messenger/const'
import { useWidgetForm, type WidgetFieldsConfig } from '@/composition/widgetForm'
import { onMounted, ref, watch } from '@/composition/vue/compositionApi'
import type { WebWidgetDetailsEmailFieldName } from '@/data/models/domain/messages/settings/webWidget/types'

const props = defineProps<{
  formValuesCache: Partial<MessengerEmailForm>
}>()

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'back'): void
  (e: 'update-form-values-cache', value: Partial<MessengerEmailForm>): void
  (e: 'resize', height: number): void
}>()

const { uniqueCode } = useWidgetStore()
const emailViewSettings = useWidgetEmailViewSettings()

const { fieldMap } = useFormFieldMap<WebWidgetDetailsEmailFieldName>(() => emailViewSettings.value.formFields)

const {
  attachments,
  uploadAttachments,
  removeAttachment,
  retryAttachment,
  getUploadedAttachments,
  setAttachments,
  clearAttachments,
} = useWidgetEmailAttachments()

let initialAttachments: WidgetAttachment[] = []
if (attachments.value.length) {
  initialAttachments = attachments.value
} else if (props.formValuesCache.attachments?.length) {
  initialAttachments = props.formValuesCache.attachments
}
setAttachments(initialAttachments)

const fieldsConfig = {
  name: {
    value: props.formValuesCache.name ?? '',
    validate: fieldMap.value.name.isRequired
      ? (value: string) => (!value ? [translateService.t('errors.name')] : [])
      : undefined,
  },
  email: {
    value: props.formValuesCache.email ?? '',
    validate: (value: string) => {
      const trimmedValue = value.trim()
      if (trimmedValue && isValidEmail(trimmedValue)) {
        return []
      }
      return [translateService.t('errors.email')]
    },
  },
  message: {
    value: props.formValuesCache.message ?? '',
    validate: (value: string) => (!value ? [translateService.t('errors.message')] : []),
  },
  attachments: {
    value: initialAttachments,
  },
} satisfies WidgetFieldsConfig<MessengerEmailForm>

const isLoading = ref(false)

const submitCallback = async (emailForm: MessengerEmailForm) => {
  try {
    isLoading.value = true
    const payload: MessengerSendEmailRequest = {
      name: emailForm.name.trim(),
      email: emailForm.email.trim(),
      message: emailForm.message.trim(),
      fileIds: emailForm.attachments.map((attachment) => attachment.id),
    }
    await messengerApiService.sendEmail(uniqueCode.value, payload)
    setFieldValue('message', '')
    clearAttachments()
  } finally {
    isLoading.value = false
  }
  emit('submit')
}

const { submit, form, commonErrors, setFieldValue } = useWidgetForm<MessengerEmailForm>(
  fieldsConfig,
  submitCallback,
  {},
  castCommonErrorToMessages,
)

watch(
  form,
  (value) => {
    emit('update-form-values-cache', {
      ...value,
      attachments: getUploadedAttachments(),
    })
  },
  { deep: true, flush: 'sync' },
)

watch(
  attachments,
  (value) => {
    setFieldValue('attachments', value)
  },
  { deep: true, flush: 'sync' },
)

const { initCommonErrorsListening } = useWidgetFormAlerts({
  scope: 'email',
  commonErrors,
})

onMounted(() => {
  initCommonErrorsListening()
})
</script>
